import React, {Component} from 'react'

import classnames from 'classnames'

import PropTypes  from 'prop-types'

import { withStyles } from '@material-ui/core/styles'

import grey from '@material-ui/core/colors/grey'

import Signal from '../../../system/signals/Signal'

import Place from '../../things/Place'

import L from 'leaflet'

import 'leaflet-easybutton'
import 'leaflet.fullscreen'
import 'leaflet.markercluster'

import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

import 'mapbox-gl-leaflet'

import 'leaflet/dist/leaflet.css'
import 'leaflet-easybutton/src/easy-button.css'
import 'leaflet.fullscreen/Control.FullScreen.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'

import marker       from 'leaflet/dist/images/marker-icon.png'
import marker2x     from 'leaflet/dist/images/marker-icon-2x.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

window.mapboxgl = mapboxgl;

const { Icon } = L ;

delete Icon.Default.prototype._getIconUrl;

Icon.Default.mergeOptions({
    iconRetinaUrl : marker2x,
    iconUrl       : marker,
    shadowUrl     : markerShadow
});

// ---------------

const styles = () =>
({
    root :
    {
        backgroundColor : grey[800] ,
        margin          : 0 ,
        padding         : 0 ,
        position        : 'absolute' ,
        width           : '100%' ,
        bottom          : 0 ,
        top             : 0
    }
});

/**
 * A Map container based on the Leaflet API.
 * @example
 * <Map
 *     className       = { classes.map }
 *     fly             = { true }
 *     fullscreenMode  = { true }
 *     gpsFixed        = { true }
 *     locale          = { locale.map }
 *     onReady         = { () => console.log( this + ' map ready') }
 *     onSelect        = { ( place ) => { history.push( this.getCurrentPath() + '/' + place.id  ) }}
 *     marker          = { false }
 *     markerDraggable = { false }
 *     markerMove      = { ( latitude , longitude ) => console.log( latitude , longitude ) }
 *     places          = { datas }
 * />
 */
class Map extends Component
{
    constructor( props )
    {
        super( props ) ;

        /**
         * The cluster reference.
         */
        this.cluster = null ;

        /**
         * The map reference.
         */
        this.map = null ;

        /**
         * The marker reference.
         * @type {L.marker}
         */
        this.marker = null ;

        this.markerMove = new Signal() ;

        this.state =
        {
            ...this.state ,
            id        : this.props.id ,
            latitude  : this.props.latitude ,
            longitude : this.props.longitude ,
            zoom      : this.props.zoom
        } ;
    }

    addMarker( latitude , longitude )
    {
        const { marker } = this.props ;
        if( marker && this.map )
        {
            const { icon , marker } = L ;

            const { markerAnchor , markerDraggable , markerSize , markerUrl } = this.props ;

            if( this.marker )
            {
                this.map.removeLayer( this.marker ) ;
                this.marker = null ;
            }

            this.marker = marker(
                [ latitude , longitude ] , {
                    draggable : markerDraggable ,
                    icon      : icon(
                    {
                        iconUrl    : markerUrl ,
                        iconSize   : markerSize ,
                        iconAnchor : markerAnchor
                    })
                }
            ) ;

            if( markerDraggable )
            {
                this.marker.on( 'drag' , this._moveMarker ) ;
            }

            this.marker.addTo( this.map );
        }

        return this ;
    }

    addSpot( place )
    {
        try
        {
            if( (place instanceof Place) && place.geo )
            {
                const { spotAnchor , spotOptions , spotSize , spotUrl } = this.props ;
                const { icon , marker } = L ;

                let image = spotUrl + place.image  ;

                let spot = marker(
                    [ place.geo.latitude, place.geo.longitude ] , {
                        icon : icon({
                            iconUrl     : image + spotOptions  ,
                            iconSize    : spotSize ,
                            iconAnchor  : spotAnchor,
                            tooltipAnchor : [ 0 , 0 ]
                        })

                    }
                );

                spot.id = place.id ;

                if( place.active )
                {
                    spot.bindTooltip( place.name ).openTooltip() ;
                    spot.on( 'click' , this._selectSpot(place) ) ;
                }

                return spot ;
            }
        }
        catch( error )
        {
            if( console )
            {
                console.warn( this + " addSpot failed, " + error.message ) ;
            }
        }

        return null ;
    }

    dispose()
    {
        if( this.marker )
        {
            this.removeMarker() ;
            this.marker = null ;
        }

        if( this.cluster )
        {
            this.cluster.clearLayers();
            if( this.map )
            {
                this.map.removeLayer( this.cluster );
            }
        }

        if( this.map )
        {
            this.map.remove() ;
            this.map = null ;
        }

        return this ;
    }

    /**
     * Fly to the specific coordinates of the map (geographical center and zoom) with the given animation options.
     * @param center The position to center the map.
     * @param zoom The zoom value to apply on the map.
     * @param options The zoom pan options.
     */
    flyTo( center , zoom , options = null )
    {
        if( this.map )
        {
            this.map.flyTo( center , zoom , options ) ;
        }
        return this ;
    }

    /**
     * Returns the geographical center of the map view.
     * @returns {LatLng} the geographical center of the map view.
     */
    getCenter()
    {
        return this.map ? this.map.getCenter() : null ;
    }

    removeMarker()
    {
        if( this.map && this.marker )
        {
            this.map.removeLayer( this.marker ) ;
            this.marker = null ;
        }
        return this ;
    }

    render()
    {
        const { classes , className , id } = this.props ;
        return <div id={id} className={ classnames( classes.root , className ) }>{""}</div> ;
    }

    /**
     * Sets the view of the map (geographical center and zoom) with the given animation options.
     * @param center The position to center the map.
     * @param zoom The zoom value to apply on the map.
     * @param options The zoom pan options.
     */
    setView( center , zoom , options = null )
    {
        if( this.map )
        {
            this.map.setView( center , zoom , options ) ;
        }
        return this ;
    }

    /**
     * Sets the zoom of the map.
     * @param zoom The zoom value to apply on the map.
     * @param options The zoom pan options.
     */
    setZoom( zoom , options = null )
    {
        if( this.map )
        {
            this.map.setZoom( zoom , options ) ;
        }
        return this ;
    }

    // ----- protected

    componentDidMount()
    {
        setTimeout( this.initialize.bind(this) , 100 ) ;
    }

    componentWillUnmount()
    {
        this.dispose() ;
    }

    initialize()
    {
        const { latitude , longitude , zoom } = this.state ;

        const {
            fullscreenControl,
            fullscreenMode,
            gpsFixed, gpsIcon, gpsIconHeight, gpsIconMargin, gpsIconWidth,
            id, locale, marker,
            maxZoom, minZoom,
            onInit, onReady,
            style, accessToken,
            scrollWheelZoom , zoomControl
        } = this.props ;

        const { easyButton , map } = L ;

        const southWest = L.latLng(-90, -180);
        const northEast = L.latLng(90, 180);

        this.map = map( id , {
            center             : [ latitude , longitude ] ,
            fullscreenControl  : fullscreenMode ? fullscreenControl : null ,
            maxBoundsViscosity : 1 ,
            zoom               : zoom,
            zoomControl        : zoomControl ,
            maxBounds          : L.latLngBounds(southWest, northEast)
        } ) ;

        if( onReady )
        {
            this.map.whenReady(onReady);
        }

        this.map.setMaxZoom( maxZoom ) ;
        this.map.setMinZoom( minZoom ) ;

        if( scrollWheelZoom )
        {
            this.map.scrollWheelZoom.enabled() ;
        }
        else
        {
            this.map.scrollWheelZoom.disable() ;
        }

        L.mapboxGL({ accessToken: accessToken, style: style}).addTo(this.map);

        if( gpsFixed )
        {
            easyButton(`<img style="margin: ${gpsIconMargin};" width=${gpsIconWidth} height=${gpsIconHeight} src=${gpsIcon}  alt="gps fixed"/>`, this._fixPosition ).addTo(this.map);
        }

        if( marker )
        {
            const geo = this.getCenter() ;
            this.addMarker( geo.lat , geo.lng );
        }

        this.map.attributionControl._attributions = {};
        this.map.attributionControl.setPrefix( false ) ;
        this.map.attributionControl.addAttribution( locale.attribution ) ;

        this.populate() ;

        if( onInit )
        {
            onInit( this.map , this ) ;
        }
    }

    populate()
    {
        if( this.map )
        {
            const { clusterSettings , places } = this.props;
            if ( places && places.length > 0 )
            {
                const { MarkerClusterGroup } = L ;

                if( this.cluster )
                {
                    this.cluster.clearLayers();
                    this.map.removeLayer( this.cluster ) ;
                }

                this.cluster = new MarkerClusterGroup( clusterSettings );

                places.forEach( ( place ) => this.cluster.addLayer( this.addSpot( place ) ) ) ;

                this.map.addLayer( this.cluster ) ;
                this.map.fitBounds( this.cluster.getBounds().pad( 0.1 ) ) ;

                const center = this.cluster.getBounds().getCenter() ;

                const zoom = this.map.getBoundsZoom( this.cluster.getBounds().pad(0.1) ) ;

                this.setState({ ...this.state, latitude  : center.lat, longitude : center.lng, zoom : zoom });

                this.addMarker(center.lat,center.lng) ;

                if( this.props.fly )
                {
                    this.flyTo( [ center.lat , center.lng ] , zoom ) ;
                }
                else
                {
                    this.setView( [ center.lat , center.lng ] , zoom ) ;
                }
            }
        }
    }

    // ----- private

    _fixPosition = () =>
    {
        const { latitude , longitude , zoom } = this.state ;
        if( this.props.fly )
        {
            this.flyTo( [ latitude , longitude ] , zoom ) ;
        }
        else
        {
            this.setView( [ latitude , longitude ] , zoom ) ;
        }
        this.addMarker(latitude,longitude) ;
    };

    _moveMarker = () =>
    {
        if( this.marker )
        {
            const { markerMove } = this.props ;

            const geo       = this.marker.getLatLng();
            const latitude  = geo.lat ;
            const longitude = geo.lng ;

            if( markerMove instanceof Function )
            {
                markerMove( latitude , longitude , this.map , this ) ;
            }
            this.setState( { ...this.state , latitude : latitude , longitude : longitude } ) ;
            this.markerMove.emit( latitude , longitude , this.map , this ) ;
        }
    };

    _selectSpot = ( place ) => () =>
    {
        const { onSelect , selectAndFly , zoom } = this.props ;
        if( place && selectAndFly )
        {
            this.flyTo( [ place.geo.latitude , place.geo.longitude ] , zoom )
        }
        if( onSelect instanceof  Function )
        {
            onSelect( place ) ;
        }
    };
}

Map.defaultProps =
{
    clusterSettings :
    {
        maxClusterRadius           : 50,
        disableClusteringAtZoom    : 20,
        spiderfyDistanceMultiplier : 2
    },
    fly               : false ,
    fullscreenControl : { position : 'topright' , pseudoFullscreen: true } ,
    fullscreenMode    : true ,
    gpsFixed          : true ,
    gpsIcon           : "./assets/images/map/gps-fixed.png" ,
    gpsIconHeight     : 20 ,
    gpsIconMargin     : "5px 0px 0px 0px" ,
    gpsIconWidth      : 20 ,
    id                : 'map' ,
    latitude          : 43.3 ,
    longitude         : 5.4 ,
    locale            : { attribution : "Ooopener" } ,
    onInit            : null ,
    onReady           : null ,
    onSelect          : null ,
    places            : null ,
    marker            : true,
    markerAnchor      : [ 16 , 38 ] ,
    markerDraggable   : false ,
    makerMove         : null ,
    markerSize        : [ 32 , 38 ] ,
    markerUrl         : './assets/images/map/marker.svg' ,
    maxZoom           : 19 ,
    minZoom           : 3 ,
    selectAndFly      : true ,
    scrollWheelZoom   : false ,
    spotAnchor        : [ 64 , 64 ] ,
    spotOptions       : '' ,
    spotSize          : [ 128 , 128 ] ,
    spotUrl           : '' ,
    zoom              : 4,
    zoomControl       : true
};

Map.propTypes =
{
    accessToken       : PropTypes.string ,
    classes           : PropTypes.object.isRequired ,
    clusterSettings   : PropTypes.object ,
    fly               : PropTypes.bool,
    fullscreenControl : PropTypes.object ,
    fullscreenMode    : PropTypes.bool ,
    gpsFixed          : PropTypes.bool,
    gpsIcon           : PropTypes.string ,
    gpsIconHeight     : PropTypes.number ,
    gpsIconMargin     : PropTypes.string ,
    gpsIconWidth      : PropTypes.number ,
    id                : PropTypes.string ,
    locale            : PropTypes.object ,
    latitude          : PropTypes.number ,
    longitude         : PropTypes.number ,
    onInit            : PropTypes.func ,
    onReady           : PropTypes.func ,
    onSelect          : PropTypes.func ,
    places            : PropTypes.arrayOf(PropTypes.instanceOf(Place)),
    marker            : PropTypes.bool,
    markerAnchor      : PropTypes.array ,
    markerDraggable   : PropTypes.bool,
    markerMove        : PropTypes.func,
    markerSize        : PropTypes.array ,
    markerUrl         : PropTypes.string,
    maxZoom           : PropTypes.number ,
    minZoom           : PropTypes.number ,
    scrollWheelZoom   : PropTypes.bool,
    selectAndFly      : PropTypes.bool,
    spotOptions       : PropTypes.string ,
    spotSize          : PropTypes.array,
    spotUrl           : PropTypes.string ,
    style             : PropTypes.string ,
    zoom              : PropTypes.number ,
    zoomControl       : PropTypes.bool

};

export default withStyles( styles ) ( Map ) ;


// WEBPACK FOOTER //
// ./src/app/components/maps/MapBoxGL.js